'use client';
import {
  KanbanBoard,
  KanbanCard,
  KanbanCards,
  KanbanHeader,
  KanbanProvider,
} from '@/components/ui/shadcn-io/kanban';
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ChangeEventHandler,
  type KeyboardEventHandler,
  type MouseEventHandler,
} from 'react';
import { BulkStatusUpdate } from './bulk-status-update';
import { cloneFeatures, columns, normalizeHubspotId } from '@/lib/kanban-data';
import { cn } from '@/lib/utils';
import type { KanbanFeature } from '@/types/kanban';
import type {
  CandidateFilters,
  CandidateStatusId,
  StageAssociationLookup,
} from '@/types/candidate';
import { createDefaultCandidateFilters } from '@/types/candidate';
import { matchesCandidateFilters } from '@/lib/candidate-filters';
import {
  useCandidateStatusUpdater,
  type CandidateStatusHistoryContext,
  type CandidateStatusUpdateOutcome,
} from '@/hooks/useCandidateStatusUpdater';
import { Button } from '@/components/ui/button';
import { ArrowDown, ArrowUp, ArrowUpDown, Loader2Icon } from 'lucide-react';
import AssociationTag from '@/components/association-tag';
import StageTransitionWarningDialog from '@/components/stage-transition-warning-dialog';
import { findStageTransitionWarningRule } from '@/lib/stage-transition-warnings';

// const dateFormatter = new Intl.DateTimeFormat('en-US', {
//   month: 'short',
//   day: 'numeric',
//   year: 'numeric',
// });

const STAGE_LABEL_BY_COLUMN: Record<CandidateStatusId, string> = {
  sollicitanten: 'Gesolliciteerd',
  uitnodigingen: 'Uitnodigen',
  'gesprek-sw': 'Gesprek S&W',
  'gesprek-opdrachtgever': 'Gesprek opdrachtgever',
  assessment: 'Assessment',
  aangenomen: 'Aangenomen',
  afgewezen: 'Afgewezen',
  teruggetrokken: 'Kandidaat teruggetrokken',
};

const SKELETON_CARD_COUNT = 3;

type StageTransitionWarningCandidate = {
  featureId: string;
  feature: KanbanFeature;
  previousFeature?: KanbanFeature;
  fromStage: CandidateStatusId | null;
  toStage: CandidateStatusId;
  description?: string;
};

type StageTransitionWarningState = StageTransitionWarningCandidate & {
  revertSnapshot: KanbanFeature[];
};

type ColumnSortMode = 'asc' | 'desc' | null;

type ColumnSelectionMeta = {
  visibleIds: string[];
  selectedCount: number;
  allSelected: boolean;
  partiallySelected: boolean;
};

const createEmptySelectionMeta = (): ColumnSelectionMeta => ({
  visibleIds: [],
  selectedCount: 0,
  allSelected: false,
  partiallySelected: false,
});


type KanbanProps = {
  features: KanbanFeature[];
  onFeaturesChange: (features: KanbanFeature[]) => void;
  filterQuery?: string;
  filters?: CandidateFilters;
  onOpenCandidate?: (candidate: KanbanFeature) => void;
  isLoading?: boolean;
  fallbackVacancyId?: string | null;
  stageAssociations?: StageAssociationLookup;
  onStatusOverride?: (payload: {
    feature: KanbanFeature;
    nextStatus: CandidateStatusId;
    statusLabel: string | null;
    associationTypeId: string | null;
  }) => void;
  onStatusSettled?: (payload: { ids: string[]; success: boolean }) => void;
  onRequestEmail?: (candidates: KanbanFeature[]) => void;
  bulkEmailDisabled?: boolean;
  historyContext?: CandidateStatusHistoryContext;
};

const Kanban = ({
  features,
  onFeaturesChange,
  filterQuery,
  filters = createDefaultCandidateFilters(),
  onOpenCandidate,
  isLoading = false,
  fallbackVacancyId = null,
  stageAssociations = {},
  onStatusOverride,
  onStatusSettled,
  onRequestEmail,
  bulkEmailDisabled = false,
  historyContext,
}: KanbanProps) => {
  const { syncStatusBatch } = useCandidateStatusUpdater();
  const [committedFeatures, setCommittedFeatures] = useState(() =>
    cloneFeatures(features)
  );
  const [internalFeatures, setInternalFeatures] = useState(() =>
    cloneFeatures(features)
  );
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [statusToUpdate, setStatusToUpdate] = useState<CandidateStatusId | null>(null);
  const [stageWarningDialog, setStageWarningDialog] = useState<StageTransitionWarningState | null>(null);
  const [columnSortDirections, setColumnSortDirections] = useState<Record<string, ColumnSortMode>>({});
  const isDraggingRef = useRef(false);
  const internalFeaturesRef = useRef(cloneFeatures(features));
  const pendingDragFeaturesRef = useRef<KanbanFeature[] | null>(null);
  const normalizedQuery = filterQuery?.trim().toLowerCase() ?? '';
  const normalizedFallbackVacancyId = useMemo(
    () => normalizeHubspotId(fallbackVacancyId),
    [fallbackVacancyId]
  );
  const mergeUpdatedFeatures = useCallback(
    (base: KanbanFeature[], updated: KanbanFeature[]) => {
      const updatedByColumn = new Map<string, KanbanFeature[]>();
      const updatedIds = new Set<string>();

      updated.forEach((feature) => {
        updatedIds.add(feature.id);
        const list = updatedByColumn.get(feature.column);
        if (list) {
          list.push(feature);
        } else {
          updatedByColumn.set(feature.column, [feature]);
        }
      });

      const columnOrder = columns.map((column) => column.id);
      const baseByColumn = new Map<string, KanbanFeature[]>();
      columnOrder.forEach((columnId) => {
        baseByColumn.set(
          columnId,
          base.filter(
            (feature) => feature.column === columnId && !updatedIds.has(feature.id)
          )
        );
      });

      const merged: KanbanFeature[] = [];
      columnOrder.forEach((columnId) => {
        const updatedForColumn = updatedByColumn.get(columnId) ?? [];
        const remainderForColumn = baseByColumn.get(columnId) ?? [];
        merged.push(...updatedForColumn, ...remainderForColumn);
      });

      return merged;
    },
    []
  );

  const commitFeatures = useCallback(
    (nextFeatures: KanbanFeature[]) => {
      pendingDragFeaturesRef.current = null;
      internalFeaturesRef.current = cloneFeatures(nextFeatures);
      setInternalFeatures(nextFeatures);
      setCommittedFeatures(cloneFeatures(nextFeatures));
      onFeaturesChange(nextFeatures);
    },
    [onFeaturesChange]
  );

  const filterFeature = useCallback(
    (feature: KanbanFeature) => {
      if (!matchesCandidateFilters(feature, filters)) {
        return false;
      }

      if (!normalizedQuery) {
        return true;
      }

      const searchTarget = `${feature.name} ${feature.owner?.name ?? ''}`.toLowerCase();
      return searchTarget.includes(normalizedQuery);
    },
    [filters, normalizedQuery]
  );

  const visibleFeatures = useMemo(
    () => internalFeatures.filter((feature) => filterFeature(feature)),
    [internalFeatures, filterFeature]
  );

  const columnSelectionState = useMemo(() => {
    const selectedSet = new Set(selectedIds);
    const state: Record<string, ColumnSelectionMeta> = {};
    columns.forEach((column) => {
      state[column.id] = createEmptySelectionMeta();
    });

    visibleFeatures.forEach((feature) => {
      const entry = state[feature.column] ?? createEmptySelectionMeta();
      if (!state[feature.column]) {
        state[feature.column] = entry;
      }
      entry.visibleIds.push(feature.id);
      if (selectedSet.has(feature.id)) {
        entry.selectedCount += 1;
      }
    });

    Object.values(state).forEach((entry) => {
      if (entry.visibleIds.length === 0) {
        entry.allSelected = false;
        entry.partiallySelected = false;
        return;
      }
      entry.allSelected = entry.selectedCount === entry.visibleIds.length;
      entry.partiallySelected = entry.selectedCount > 0 && !entry.allSelected;
    });

    return state;
  }, [visibleFeatures, selectedIds]);

  const orderedVisibleFeatures = useMemo(() => {
    if (Object.keys(columnSortDirections).length === 0) {
      return visibleFeatures;
    }

    const featuresByColumn = new Map<string, KanbanFeature[]>();
    columns.forEach((column) => {
      featuresByColumn.set(column.id, []);
    });

    visibleFeatures.forEach((feature) => {
      const list = featuresByColumn.get(feature.column);
      if (list) {
        list.push(feature);
      } else {
        featuresByColumn.set(feature.column, [feature]);
      }
    });

    const ordered: KanbanFeature[] = [];
    columns.forEach((column) => {
      const columnFeatures = featuresByColumn.get(column.id) ?? [];
      const direction = columnSortDirections[column.id];
      if (!direction) {
        ordered.push(...columnFeatures);
        return;
      }
      const multiplier = direction === 'asc' ? 1 : -1;
      const sortedColumn = [...columnFeatures].sort((a, b) => {
        return (
          String(a.name ?? '').localeCompare(String(b.name ?? ''), 'nl', {
            sensitivity: 'base',
          }) * multiplier
        );
      });
      ordered.push(...sortedColumn);
    });

    return ordered;
  }, [visibleFeatures, columnSortDirections]);

  const toggleColumnSort = useCallback((columnId: string) => {
    setColumnSortDirections((prevDirections) => {
      const nextDirections = { ...prevDirections };
      const currentDirection = nextDirections[columnId] ?? null;
      let nextDirection: ColumnSortMode;
      if (currentDirection === 'asc') {
        nextDirection = 'desc';
      } else if (currentDirection === 'desc') {
        nextDirection = null;
      } else {
        nextDirection = 'asc';
      }

      if (nextDirection === null) {
        delete nextDirections[columnId];
      } else {
        nextDirections[columnId] = nextDirection;
      }

      return nextDirections;
    });
  }, []);

  const renderNameSortIcon = (direction?: ColumnSortMode) => {
    if (direction === 'asc') {
      return <ArrowUp className="h-3.5 w-3.5" aria-hidden />;
    }
    if (direction === 'desc') {
      return <ArrowDown className="h-3.5 w-3.5" aria-hidden />;
    }
    return <ArrowUpDown className="h-3.5 w-3.5 opacity-60" aria-hidden />;
  };

  const visibleCommittedFeatures = useMemo(
    () => committedFeatures.filter((feature) => filterFeature(feature)),
    [committedFeatures, filterFeature]
  );

  const handleDataChange = useCallback(
    (updatedFeatures: KanbanFeature[]) => {
      console.log('[kanban] handleDataChange triggered', {
        updatedFeatureIds: updatedFeatures.map((feature) => feature.id),
        updatedFeatureColumns: updatedFeatures.map((feature) => feature.column),
      });
      const mergedFeatures = mergeUpdatedFeatures(
        internalFeaturesRef.current,
        updatedFeatures
      );

      setSelectedIds((prevSelected) =>
        prevSelected.filter((id) =>
          mergedFeatures.some((feature) => feature.id === id)
        )
      );

      if (isDraggingRef.current) {
        pendingDragFeaturesRef.current = mergedFeatures;
        internalFeaturesRef.current = cloneFeatures(mergedFeatures);
        setInternalFeatures(mergedFeatures);
        return;
      }

      commitFeatures(mergedFeatures);
    },
    [commitFeatures, mergeUpdatedFeatures]
  );

  const handleDragStart = () => {
    isDraggingRef.current = true;
  };

  const handleDragEnd = () => {
    isDraggingRef.current = false;
    const pendingFeatures = pendingDragFeaturesRef.current;
    if (!pendingFeatures) {
      return;
    }

    const previousSnapshot = cloneFeatures(committedFeatures);
    const previousById = new Map(previousSnapshot.map((feature) => [feature.id, feature]));
    const optimisticSnapshot = cloneFeatures(pendingFeatures).map((feature) => {
      const previous = previousById.get(feature.id);
      const hasMoved = previous ? previous.column !== feature.column : false;
      if (!hasMoved) {
        return feature;
      }
      const association = stageAssociations[feature.column];
      const stageLabelFallback =
        STAGE_LABEL_BY_COLUMN[feature.column as CandidateStatusId] ?? feature.statusLabel ?? null;
      return {
        ...feature,
        associationTypeId: association?.typeId ?? feature.associationTypeId ?? null,
        statusLabel: association?.label ?? stageLabelFallback,
      };
    });

    const movedFeatures = optimisticSnapshot.filter((feature) => {
      const previous = previousById.get(feature.id);
      return previous ? previous.column !== feature.column : false;
    });
    const warningCandidates = movedFeatures
      .map((feature) => {
        const previous = previousById.get(feature.id);
        const fromStage = (previous?.column as CandidateStatusId | undefined) ?? null;
        const toStage = feature.column as CandidateStatusId;
        const rule = findStageTransitionWarningRule(fromStage, toStage);
        if (!rule) {
          return null;
        }

        return {
          featureId: feature.id,
          feature,
          previousFeature: previous,
          fromStage,
          toStage,
          description: rule.description,
        } satisfies StageTransitionWarningCandidate;
      })
      .filter(Boolean) as StageTransitionWarningCandidate[];

    const candidate = warningCandidates[0];
    if (candidate) {
      setStageWarningDialog({
        ...candidate,
        revertSnapshot: previousSnapshot,
      });
    }

    const warningFeatureIds = new Set(warningCandidates.map((candidate) => candidate.featureId));
    const immediateFeatures = movedFeatures.filter(
      (feature) => !warningFeatureIds.has(feature.id)
    );

    console.log('[kanban] applying pending features after drag end', {
      candidateIds: optimisticSnapshot.map((feature) => feature.id),
      columns: optimisticSnapshot.map((feature) => feature.column),
      movedCandidateIds: movedFeatures.map((feature) => feature.id),
    });

    commitFeatures(optimisticSnapshot);

    if (!movedFeatures.length) {
      return;
    }

    immediateFeatures.forEach((feature) => {
      const association = stageAssociations[feature.column];
      const candidateLabel =
        association?.label ??
        STAGE_LABEL_BY_COLUMN[feature.column as CandidateStatusId] ??
        feature.statusLabel ??
        null;
      const associationTypeId = association?.typeId ?? feature.associationTypeId ?? null;
      onStatusOverride?.({
        feature,
        nextStatus: feature.column,
        statusLabel: candidateLabel,
        associationTypeId,
      });
    });

    const immediateMovedIds = immediateFeatures.map((feature) => feature.id);

    if (immediateFeatures.length) {
      void (async () => {
        const results = await syncStatusBatch(
          immediateFeatures.map((feature) => {
            const previous = previousById.get(feature.id);
            if (!previous) {
              return {
                feature,
                nextStatus: feature.column,
                previousFeature: undefined,
                fallbackVacancyId: normalizedFallbackVacancyId,
                stageAssociations,
                historyContext,
              };
            }
            return {
              feature,
              nextStatus: feature.column,
              previousFeature: previous,
              fallbackVacancyId: normalizedFallbackVacancyId,
              stageAssociations,
              historyContext,
            };
          })
        );

        const shouldRevert = results.some(
          (result) => result.status !== 'fulfilled'
        );
        if (shouldRevert) {
          onStatusSettled?.({ ids: immediateMovedIds, success: false });
          console.warn('[kanban] reverting UI after status sync issues', {
            results: results.map((result) => ({
              featureId: result.feature.id,
              status: result.status,
              reason:
                result.status === 'fulfilled'
                  ? undefined
                  : result.reason instanceof Error
                    ? result.reason.message
                    : String(result.reason),
            })),
          });
          commitFeatures(previousSnapshot);
          return;
        }

        const fulfilledLabels = results.filter(
          (
            result
          ): result is Extract<CandidateStatusUpdateOutcome, { status: 'fulfilled' }> =>
            result.status === 'fulfilled'
        );

        if (fulfilledLabels.length) {
          const fulfilledMap = new Map(
            fulfilledLabels.map((entry) => [entry.feature.id, entry] as const)
          );
          const updated = internalFeaturesRef.current.map((feature) => {
            const applied = fulfilledMap.get(feature.id);
            if (!applied) {
              return feature;
            }
            const association = stageAssociations[feature.column];
            return {
              ...feature,
              statusLabel: applied.appliedLabel,
              associationTypeId:
                applied.associationTypeId ??
                association?.typeId ??
                feature.associationTypeId ??
                null,
            };
          });
          commitFeatures(updated);
        }

        onStatusSettled?.({ ids: immediateMovedIds, success: true });
      })();
    }
  };

  const handleDragCancel = () => {
    isDraggingRef.current = false;
    const snapshot = cloneFeatures(committedFeatures);
    commitFeatures(snapshot);
    setSelectedIds((prevSelected) =>
      prevSelected.filter((id) => snapshot.some((feature) => feature.id === id))
    );
  };

  const handleStageWarningCancel = () => {
    if (!stageWarningDialog) {
      return;
    }
    const warningData = stageWarningDialog;
    setStageWarningDialog(null);
    commitFeatures(cloneFeatures(warningData.revertSnapshot));
  };

  const handleStageWarningConfirm = () => {
    if (!stageWarningDialog) {
      return;
    }
    const warningData = stageWarningDialog;
    setStageWarningDialog(null);

    const currentFeature =
      internalFeaturesRef.current.find((feature) => feature.id === warningData.featureId) ??
      warningData.feature;

    const association = stageAssociations[currentFeature.column];
    const stageLabelFallback =
      STAGE_LABEL_BY_COLUMN[currentFeature.column as CandidateStatusId] ??
      currentFeature.statusLabel ??
      null;
    const candidateLabel = association?.label ?? stageLabelFallback;
    const associationTypeId = association?.typeId ?? currentFeature.associationTypeId ?? null;

    onStatusOverride?.({
      feature: currentFeature,
      nextStatus: currentFeature.column as CandidateStatusId,
      statusLabel: candidateLabel,
      associationTypeId,
    });

    const pendingUpdates = [
      {
        feature: currentFeature,
        nextStatus: currentFeature.column as CandidateStatusId,
        previousFeature: warningData.previousFeature,
        fallbackVacancyId: normalizedFallbackVacancyId,
        stageAssociations,
        historyContext,
      },
    ];

    const pendingIds = [currentFeature.id];

    void (async () => {
      const results = await syncStatusBatch(pendingUpdates);
      const shouldRevert = results.some((result) => result.status !== 'fulfilled');
      if (shouldRevert) {
        onStatusSettled?.({ ids: pendingIds, success: false });
        console.warn('[kanban] reverting UI after warning confirmation issues', {
          results: results.map((result) => ({
            featureId: result.feature.id,
            status: result.status,
            reason:
              result.status === 'fulfilled'
                ? undefined
                : result.reason instanceof Error
                  ? result.reason.message
                  : String(result.reason),
          })),
        });
        commitFeatures(cloneFeatures(warningData.revertSnapshot));
        return;
      }

      const fulfilled = results.filter(
        (
          result
        ): result is Extract<CandidateStatusUpdateOutcome, { status: 'fulfilled' }> =>
          result.status === 'fulfilled'
      );

      if (fulfilled.length) {
        const fulfilledMap = new Map(
          fulfilled.map((entry) => [entry.feature.id, entry] as const)
        );
        const updated = internalFeaturesRef.current.map((feature) => {
          const applied = fulfilledMap.get(feature.id);
          if (!applied) {
            return feature;
          }
          const associationMatch = stageAssociations[feature.column];
          return {
            ...feature,
            statusLabel: applied.appliedLabel,
            associationTypeId:
              applied.associationTypeId ??
              associationMatch?.typeId ??
              feature.associationTypeId ??
              null,
          };
        });
        commitFeatures(updated);
      }

      onStatusSettled?.({ ids: pendingIds, success: true });
    })();
  };

  const handleSelectToggle = useCallback((id: string, isSelected: boolean) => {
    setSelectedIds((prevSelected) => {
      if (isSelected) {
        if (prevSelected.includes(id)) {
          return prevSelected;
        }
        return [...prevSelected, id];
      }
      return prevSelected.filter((selectedId) => selectedId !== id);
    });
  }, []);

  const handleColumnSelectAll = useCallback(
    (columnId: string, isChecked: boolean) => {
      const columnState = columnSelectionState[columnId];
      if (!columnState || columnState.visibleIds.length === 0) {
        return;
      }

      setSelectedIds((prevSelected) => {
        const nextSet = new Set(prevSelected);
        columnState.visibleIds.forEach((candidateId) => {
          if (isChecked) {
            nextSet.add(candidateId);
          } else {
            nextSet.delete(candidateId);
          }
        });
        return Array.from(nextSet);
      });
    },
    [columnSelectionState]
  );

  const selectedCount = selectedIds.length;

  const selectedFeatures = useMemo(() => {
    if (selectedIds.length === 0) {
      return [];
    }
    const featureMap = new Map(internalFeatures.map((feature) => [feature.id, feature]));
    return selectedIds
      .map((id) => featureMap.get(id))
      .filter((feature): feature is KanbanFeature => Boolean(feature));
  }, [internalFeatures, selectedIds]);

  const selectedFeaturesWithEmail = useMemo(
    () =>
      selectedFeatures.filter((feature) => {
        const email = feature.email?.trim();
        return Boolean(email);
      }),
    [selectedFeatures]
  );

  useEffect(() => {
    if (selectedIds.length === 0 && statusToUpdate !== null) {
      setStatusToUpdate(null);
    }
  }, [selectedIds.length, statusToUpdate]);

  const handleBulkEmail = () => {
    if (!onRequestEmail || selectedFeaturesWithEmail.length === 0) {
      return;
    }
    onRequestEmail(selectedFeaturesWithEmail);
  };

  const applyStatusChange = () => {
    if (selectedIds.length === 0 || !statusToUpdate) {
      return;
    }

    const selectedIdSet = new Set(selectedIds);
    const previousSnapshot = cloneFeatures(internalFeaturesRef.current);
    const updatedFeatures = internalFeaturesRef.current.map((feature) => {
      if (!selectedIdSet.has(feature.id)) {
        return feature;
      }
      const association = stageAssociations[statusToUpdate];
      const fallbackStageLabel =
        STAGE_LABEL_BY_COLUMN[statusToUpdate] ?? feature.statusLabel ?? null;
      const candidateLabel = association?.label ?? fallbackStageLabel;
      const associationTypeId = association?.typeId ?? feature.associationTypeId ?? null;
      return {
        ...feature,
        column: statusToUpdate,
        statusLabel: candidateLabel,
        associationTypeId,
      };
    });

    commitFeatures(updatedFeatures);
    setSelectedIds([]);
    setStatusToUpdate(null);

    const pendingUpdates = updatedFeatures
      .filter((feature) => selectedIdSet.has(feature.id))
      .map((feature) => {
        const previous = previousSnapshot.find((item) => item.id === feature.id);
        return {
          feature,
          nextStatus: feature.column,
          previousFeature: previous,
          fallbackVacancyId: normalizedFallbackVacancyId,
          stageAssociations,
          historyContext,
        };
      });

    if (!pendingUpdates.length) {
      return;
    }

    pendingUpdates.forEach(({ feature }) => {
      const association = stageAssociations[feature.column];
      const candidateLabel = association?.label ?? feature.statusLabel ?? null;
      const associationTypeId = association?.typeId ?? feature.associationTypeId ?? null;
      onStatusOverride?.({
        feature,
        nextStatus: feature.column,
        statusLabel: candidateLabel,
        associationTypeId,
      });
    });

    const pendingIds = pendingUpdates.map(({ feature }) => feature.id);

    void (async () => {
      const results = await syncStatusBatch(pendingUpdates);
      const shouldRevert = results.some((result) => result.status !== 'fulfilled');
      if (shouldRevert) {
        onStatusSettled?.({ ids: pendingIds, success: false });
        console.warn('[kanban] reverting UI after bulk status sync issues', {
          results: results.map((result) => ({
            featureId: result.feature.id,
            status: result.status,
            reason:
              result.status === 'fulfilled'
                ? undefined
                : result.reason instanceof Error
                  ? result.reason.message
                  : String(result.reason),
          })),
        });
        commitFeatures(previousSnapshot);
        return;
      }

      const fulfilled = results.filter(
        (
          result
        ): result is Extract<CandidateStatusUpdateOutcome, { status: 'fulfilled' }> =>
          result.status === 'fulfilled'
      );

      if (fulfilled.length === 0) {
        onStatusSettled?.({ ids: pendingIds, success: true });
        return;
      }

      const fulfilledMap = new Map(
        fulfilled.map((entry) => [entry.feature.id, entry] as const)
      );

      const updated = internalFeaturesRef.current.map((feature) => {
        const applied = fulfilledMap.get(feature.id);
        if (!applied) {
          return feature;
        }
        const association = stageAssociations[feature.column];
        return {
          ...feature,
          statusLabel: applied.appliedLabel,
          associationTypeId:
            applied.associationTypeId ??
            association?.typeId ??
            feature.associationTypeId ??
            null,
        };
      });
      commitFeatures(updated);
      onStatusSettled?.({ ids: pendingIds, success: true });
    })();
  };

  useEffect(() => {
    if (!isDraggingRef.current) {
      const cloned = cloneFeatures(features);
      internalFeaturesRef.current = cloned;
      setInternalFeatures(cloned);
      setCommittedFeatures(cloneFeatures(features));
      pendingDragFeaturesRef.current = null;
    }
    setSelectedIds((prevSelected) =>
      prevSelected.filter((id) =>
        features.some((feature) => feature.id === id)
      )
    );
  }, [features]);

  useEffect(() => {
    if (!normalizedQuery) {
      return;
    }
    const visibleIds = new Set(visibleFeatures.map((feature) => feature.id));
    setSelectedIds((prevSelected) =>
      prevSelected.filter((id) => visibleIds.has(id))
    );
  }, [normalizedQuery, visibleFeatures]);

  const ColumnSelectAllToggle = ({
    columnId,
    columnName,
    selection,
  }: {
    columnId: string;
    columnName: string;
    selection?: ColumnSelectionMeta;
  }) => {
    if (!selection || selection.selectedCount === 0) {
      return null;
    }

    const handleChange: ChangeEventHandler<HTMLInputElement> = (event) => {
      handleColumnSelectAll(columnId, event.target.checked);
    };

    return (
      <input
        type="checkbox"
        aria-label={`Selecteer alle kandidaten in ${columnName}`}
        className="h-4 w-4"
        checked={selection.allSelected}
        onChange={handleChange}
        onClick={(event) => event.stopPropagation()}
        onMouseDown={(event) => event.stopPropagation()}
        onPointerDown={(event) => event.stopPropagation()}
      />
    );
  };

  const FeatureCard = ({
    feature,
    columnId,
  }: {
    feature: KanbanFeature;
    columnId: string;
  }) => {
    const isPending = feature.isStatusOverridePending === true;
    const handleClick: MouseEventHandler<HTMLDivElement> = (event) => {
      if (event.defaultPrevented) {
        return;
      }

      const target = event.target as HTMLElement | null;
      if (!target) {
        return;
      }

      if (target.closest('input, button')) {
        return;
      }

      onOpenCandidate?.(feature);
    };
    const manualTags = feature.associationTags ?? [];

    const handleKeyDown: KeyboardEventHandler<HTMLDivElement> = (event) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        const target = event.target as HTMLElement | null;
        if (target && target.closest('input, button')) {
          return;
        }
        onOpenCandidate?.(feature);
      }
    };

    return (
      <KanbanCard
        column={columnId}
        id={feature.id}
        key={feature.id}
        name={feature.name}
        className={cn(
          'bg-white border-zinc-200 shadow-none transition-shadow duration-300 ease-in-out',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/60',
          isPending && 'kanban-card-pending bg-white/90'
        )}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        aria-busy={isPending}
        role="button"
        tabIndex={0}
      >
        <div className="flex items-start justify-between gap-2">
          <div className="flex flex-col gap-2">
            <p className="m-0 flex-1 font-medium text-sm">
              {feature.name}
            </p>
            {manualTags.length ? (
              <div className="flex flex-wrap gap-1">
                {manualTags.map((tag) =>
                  tag.label ? (
                    <AssociationTag
                      key={`${feature.id}-${tag.slug ?? tag.label}`}
                      label={tag.label}
                      className="mt-0.5"
                    />
                  ) : null
                )}
              </div>
            ) : null}
          </div>
          {isPending ? (
            <span
              className="flex h-4 w-4 shrink-0 items-center justify-center text-primary"
              role="status"
              aria-live="polite"
            >
              <Loader2Icon className="h-4 w-4 animate-spin" aria-hidden="true" />
              <span className="sr-only">Statusupdate wordt verwerkt</span>
            </span>
          ) : (
            <input
              aria-label="Markeer kandidaat"
              className="h-4 w-4 shrink-0"
              type="checkbox"
              checked={selectedIds.includes(feature.id)}
              onClick={(event) => event.stopPropagation()}
              onMouseDown={(event) => event.stopPropagation()}
              onPointerDown={(event) => event.stopPropagation()}
              onChange={(event) =>
                handleSelectToggle(feature.id, event.target.checked)
              }
            />
          )}
        </div>
        {/* DATE ERUIT */}
        {/* <p className="m-0 text-slate-500 text-xs">
          {dateFormatter.format(feature.startAt)}
        </p> */}
      </KanbanCard>
    );
  };

  const ColumnSkeleton = () => (
    <div className="flex flex-1 flex-col gap-2 p-2" aria-hidden="true">
      {Array.from({ length: SKELETON_CARD_COUNT }).map((_, index) => (
        <div
          key={index}
          className="rounded-md border border-zinc-200 bg-white/80 p-3 shadow-sm"
        >
          <span className="block h-4 w-3/4 skeleton-shimmer" />
          <span className="mt-3 block h-3 w-1/2 skeleton-shimmer" />
        </div>
      ))}
    </div>
  );

  return (
    <div className="custom-scrollbar flex h-full w-full min-h-0 flex-col overflow-hidden pb-6 select-none">
      <KanbanProvider
        columns={columns}
        data={isLoading ? [] : orderedVisibleFeatures}
        onDataChange={handleDataChange}
        onDragStart={() => handleDragStart()}
        onDragEnd={() => handleDragEnd()}
        onDragCancel={() => handleDragCancel()}
        className="mx-6 h-full pb-2"
      >
        {(column) => {
          const columnFeatureCount = visibleCommittedFeatures.filter(
            (feature) => feature.column === column.id
          ).length;

          return (
            <KanbanBoard id={column.id} key={column.id}
              className="bg-primary-bg ring-0 border-zinc-200 shadow-none">
              <KanbanHeader className="flex items-center justify-between gap-2 border-zinc-200">
                <div className="flex items-center gap-2">
                  <div
                    className={cn(
                      'h-2 w-2 rounded-full',
                      column.indicatorClass
                    )}
                  />
                  <div className="flex items-center gap-1.5">
                    <span>{column.name}</span>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6 px-0 text-slate-500 hover:text-slate-900"
                      onClick={() => toggleColumnSort(column.id)}
                      aria-label="Sorteer kandidaten op naam"
                      aria-pressed={Boolean(columnSortDirections[column.id])}
                    >
                      {renderNameSortIcon(columnSortDirections[column.id])}
                    </Button>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-slate-500 text-xs">
                    {isLoading ? '…' : columnFeatureCount}
                  </span>
                  <ColumnSelectAllToggle
                    columnId={column.id}
                    columnName={column.name}
                    selection={columnSelectionState[column.id]}
                  />
                </div>
              </KanbanHeader>
              {isLoading ? (
                <ColumnSkeleton />
              ) : (
                <KanbanCards id={column.id}>
                  {(feature: KanbanFeature) => (
                    <FeatureCard key={feature.id} feature={feature} columnId={column.id} />
                  )}
                </KanbanCards>
              )}
            </KanbanBoard>
          );
        }}
      </KanbanProvider>
      <BulkStatusUpdate
        columns={columns}
        statusToUpdate={statusToUpdate}
        onStatusChange={setStatusToUpdate}
        onApply={applyStatusChange}
        selectedCount={selectedCount}
        onClearSelection={() => {
          setSelectedIds([]);
          setStatusToUpdate(null);
        }}
        onEmailSend={onRequestEmail ? handleBulkEmail : undefined}
        emailDisabled={
          selectedFeaturesWithEmail.length === 0 || bulkEmailDisabled
        }
      />
      <StageTransitionWarningDialog
        open={Boolean(stageWarningDialog)}
        fromStage={stageWarningDialog?.fromStage ?? null}
        toStage={stageWarningDialog?.toStage ?? null}
        description={stageWarningDialog?.description}
        onCancel={handleStageWarningCancel}
        onConfirm={handleStageWarningConfirm}
      />
    </div>
  );
};

export default Kanban;
